import { useState, useEffect, useCallback } from 'react'
import { doc, onSnapshot, collection, query, orderBy, limit as fbLimit } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from './useAuth'
import { getCreditHistory } from '../services/credits'

/**
 * Hook to manage user credits
 * Provides real-time balance updates and usage methods
 */
export function useCredits() {
  const { user } = useAuth()
  const [credits, setCredits] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [error, setError] = useState(null)

  // Subscribe to real-time credit balance updates
  useEffect(() => {
    if (!user) {
      setCredits(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const monetizationRef = doc(db, 'users', user.uid, 'monetization', 'status')

    const unsubscribe = onSnapshot(
      monetizationRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setCredits({
            balance: data.creditsBalance || 0,
            allottedMonthly: data.creditsAllottedMonthly || 0,
            usedThisMonth: data.creditsUsedThisMonth || 0,
            purchased: data.creditsPurchased || 0,
            lastReset: data.lastCreditReset?.toDate() || null,
            plan: data.plan || 'free',
          })
        } else {
          setCredits({
            balance: 0,
            allottedMonthly: 0,
            usedThisMonth: 0,
            purchased: 0,
            lastReset: null,
            plan: 'free',
          })
        }
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Credits listener error:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user])

  // Load credit history
  const loadHistory = useCallback(async (limit = 50) => {
    if (!user) return

    setHistoryLoading(true)
    try {
      const result = await getCreditHistory(limit)
      setHistory(result.history || [])
    } catch (err) {
      console.error('Failed to load credit history:', err)
      setError(err.message)
    } finally {
      setHistoryLoading(false)
    }
  }, [user])

  // Subscribe to real-time history updates
  useEffect(() => {
    if (!user) {
      setHistory([])
      return
    }

    const historyRef = collection(db, 'users', user.uid, 'creditHistory')
    const historyQuery = query(historyRef, orderBy('date', 'desc'), fbLimit(20))

    const unsubscribe = onSnapshot(
      historyQuery,
      (snapshot) => {
        const historyData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || null,
        }))
        setHistory(historyData)
      },
      (err) => {
        console.error('History listener error:', err)
      }
    )

    return () => unsubscribe()
  }, [user])

  // Check if user has sufficient credits
  const hasSufficientCredits = useCallback((required) => {
    return credits?.balance >= required
  }, [credits])

  // Calculate remaining credits this month
  const remainingThisMonth = credits 
    ? credits.allottedMonthly - credits.usedThisMonth 
    : 0

  // Calculate usage percentage
  const usagePercentage = credits?.allottedMonthly > 0
    ? Math.round((credits.usedThisMonth / credits.allottedMonthly) * 100)
    : 0

  // Check if low on credits (less than 20% remaining)
  const isLowOnCredits = credits?.balance > 0 && credits.balance < (credits.allottedMonthly * 0.2)

  return {
    credits,
    history,
    loading,
    historyLoading,
    error,
    // Computed
    balance: credits?.balance || 0,
    remainingThisMonth,
    usagePercentage,
    isLowOnCredits,
    // Methods
    loadHistory,
    hasSufficientCredits,
  }
}

export default useCredits
